<template>
  <div v-if="open" class="assistant-overlay" @click.self="$emit('close')">
    <aside class="assistant-drawer">
      <header class="assistant-header">
        <h3>UEFA Auditor</h3>
        <button class="close-btn" @click="$emit('close')">×</button>
      </header>

      <div class="assistant-controls">
        <div class="field">
          <label>Category</label>
          <div class="segmented">
            <button
              :class="['seg-btn', { active: category === 'men' }]"
              @click="category = 'men'"
            >
              Men
            </button>
            <button
              :class="['seg-btn', { active: category === 'women' }]"
              @click="category = 'women'"
            >
              Women
            </button>
          </div>
        </div>
        <div class="field">
          <label>Season</label>
          <input v-model.trim="season" type="text" placeholder="2026/2027" />
        </div>
      </div>

      <div class="assistant-prompt">
        <label>Question</label>
        <textarea
          v-model.trim="prompt"
          rows="4"
          placeholder="Why does this team have these points?"
          @keydown.ctrl.enter.prevent="runQuery"
        />
        <div class="prompt-actions">
          <button class="query-btn" :disabled="loading || !prompt || !season" @click="runQuery">
            {{ loading ? 'Analyzing...' : 'Ask' }}
          </button>
          <button class="clear-btn" :disabled="loading" @click="clearAll">Clear</button>
        </div>
        <div class="quick-prompts">
          <button class="quick-btn" @click="setQuickPrompt('Why does this team have these points?')">Explain points</button>
          <button class="quick-btn" @click="setQuickPrompt('What changed today for this team?')">Today's changes</button>
          <button class="quick-btn" @click="setQuickPrompt('Which rule was applied to the last match?')">Audit rule</button>
          <button class="quick-btn" @click="setQuickPrompt('Why does this team differ from Kassiesa?')">Discrepancy</button>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <section v-if="response" class="assistant-result">
        <p class="intent-badge">Query: {{ response.intent || 'explain_points' }}</p>
        <p v-if="response.audit" class="audit-badge">
          {{ response.audit.mode === 'openai' ? 'AI auditor' : 'Local auditor' }} · verified local evidence
        </p>
        <p class="answer">{{ response.answer }}</p>

        <div v-if="response.evidence?.summary" class="summary-grid">
          <div class="summary-item"><span>Points</span><strong>{{ fmt(response.evidence.summary.points) }}</strong></div>
          <div class="summary-item"><span>Match</span><strong>{{ fmt(response.evidence.summary.matchPoints) }}</strong></div>
          <div class="summary-item"><span>Bonus</span><strong>{{ fmt(response.evidence.summary.bonus) }}</strong></div>
          <div v-if="response.evidence.summary.naPoints !== undefined" class="summary-item">
            <span>20% NA</span><strong>{{ fmt(response.evidence.summary.naPoints) }}</strong>
          </div>
        </div>

        <div v-if="response.evidence?.latestDelta" class="evidence-card">
          <h4>Latest change</h4>
          <p>
            Day: <strong>{{ response.evidence.latestDelta.latestDay }}</strong>
            · Change: <strong>{{ fmt(response.evidence.latestDelta.delta) }}</strong>
            · Events: <strong>{{ response.evidence.latestDelta.rowsCount }}</strong>
          </p>
        </div>

        <div v-if="response.evidence?.ruleAudit" class="evidence-card">
          <h4>Rule audit</h4>
          <p>{{ response.evidence.ruleAudit.ruleText }}</p>
          <p class="rule-meta">
            Date: {{ shortDate(response.evidence.ruleAudit.date) }} ·
            Stage: {{ response.evidence.ruleAudit.stage || '-' }} ·
            Result: {{ response.evidence.ruleAudit.result || '-' }} ·
            Status: {{ response.evidence.ruleAudit.status || '-' }} ·
            Points: {{ fmt(response.evidence.ruleAudit.points) }}
          </p>
        </div>

        <div v-if="response.evidence?.standingsPositionExplain" class="evidence-card">
          <h4>Position Explain</h4>
          <p>
            Rank: <strong>#{{ response.evidence.standingsPositionExplain.rank ?? '-' }}</strong>
            · Played: <strong>{{ response.evidence.standingsPositionExplain.played ?? '-' }}</strong>
          </p>
          <p v-if="(response.evidence.standingsPositionExplain.decisiveComparisons || []).length">
            {{ response.evidence.standingsPositionExplain.decisiveComparisons[0].text }}
          </p>
          <p class="rule-meta">
            Criteria order:
            {{
              (response.evidence.standingsPositionExplain.criteriaOrder || [])
                .map((c) => `${c.label}${c.available ? '' : ' (N/A)'}`)
                .join(' > ')
            }}
          </p>
        </div>

        <div v-if="response.evidence?.mismatchDiagnosis" class="evidence-card">
          <h4>Discrepancy diagnosis</h4>
          <p>Reason: <strong>{{ response.evidence.mismatchDiagnosis.reason || '-' }}</strong></p>
          <p class="rule-meta">
            Source: {{ (response.evidence.mismatchDiagnosis.source || '-').toUpperCase() }} ·
            Current-season difference: {{ fmt(response.evidence.mismatchDiagnosis.deltaCurrentSeason) }}
          </p>
        </div>

        <div v-if="(response.evidence?.matchBreakdown || []).length" class="evidence-card">
          <h4>Match breakdown</h4>
          <table>
            <thead><tr><th>Date</th><th>Comp.</th><th>Round</th><th>Points</th></tr></thead>
            <tbody>
              <tr v-for="(row, idx) in response.evidence.matchBreakdown" :key="`m-${idx}`">
                <td>{{ shortDate(row.date) }}</td>
                <td>{{ row.competition || '-' }}</td>
                <td>{{ row.round || '-' }}</td>
                <td>{{ fmt(row.points) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="(response.evidence?.bonusBreakdown || []).length" class="evidence-card">
          <h4>Bonus breakdown</h4>
          <table>
            <thead><tr><th>Comp.</th><th>Source</th><th>Description</th><th>Points</th></tr></thead>
            <tbody>
              <tr v-for="(row, idx) in response.evidence.bonusBreakdown" :key="`b-${idx}`">
                <td>{{ row.competition || '-' }}</td>
                <td>{{ row.source || '-' }}</td>
                <td>{{ row.label || '-' }}</td>
                <td>{{ fmt(row.points) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="(response?.suggestions || []).length" class="suggestions">
        <h4>Suggestions</h4>
        <button
          v-for="(item, idx) in response.suggestions"
          :key="`s-${idx}`"
          class="suggestion-btn"
          @click="prompt = item.replace(/^Try:\s*/i, ''); runQuery()"
        >
          {{ item }}
        </button>
      </section>
    </aside>
  </div>
</template>

<script>
import apiService from '@/services/api'

export default {
  name: 'AIAssistantDrawer',
  props: {
    open: { type: Boolean, default: false },
    initialSeason: { type: String, default: '2026/2027' },
    request: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      error: '',
      response: null,
      prompt: '',
      season: this.initialSeason,
      category: 'men',
      queryContext: null
    }
  },
  watch: {
    request: {
      deep: true,
      handler(value) {
        if (!value || typeof value !== 'object') return
        const prompt = String(value.prompt || '').trim()
        const season = String(value.season || '').trim()
        const category = String(value.category || '').toLowerCase() === 'women' ? 'women' : 'men'
        this.prompt = prompt || this.prompt
        this.season = season || this.season
        this.category = category
        this.queryContext = value.context && typeof value.context === 'object' ? value.context : null
        if (value.autoRun && this.open && this.prompt && this.season) {
          this.runQuery()
        }
      }
    },
    open(value) {
      if (value && this.request?.autoRun && this.prompt && this.season) {
        this.runQuery()
      }
    }
  },
  methods: {
    async runQuery() {
      if (!this.prompt || !this.season) return
      this.loading = true
      this.error = ''
      try {
        const data = await apiService.queryUefaAssistant({
          prompt: this.prompt,
          season: this.season,
          category: this.category,
          context: this.queryContext
        })
        if (!data?.success) throw new Error(data?.error || 'Query failed.')
        this.response = data
      } catch (err) {
        this.error = err?.message || 'Query failed.'
      } finally {
        this.loading = false
      }
    },
    clearAll() {
      this.response = null
      this.error = ''
      this.prompt = ''
      this.queryContext = null
    },
    setQuickPrompt(text) {
      this.prompt = text
    },
    fmt(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return (Math.trunc(num * 1000) / 1000).toFixed(3)
    },
    shortDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      return `${day}/${month}`
    }
  }
}
</script>

<style scoped>
.assistant-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  z-index: 200;
}

.assistant-drawer {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: min(760px, 48vw);
  min-width: 460px;
  background: #ffffff;
  border-left: 1px solid #d8e0ec;
  box-shadow: -18px 0 28px rgba(2, 8, 23, 0.25);
  padding: 0.9rem;
  overflow-y: auto;
}

.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.assistant-header h3 { margin: 0; }

.close-btn {
  border: none;
  background: #e2e8f0;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  font-size: 1.1rem;
  cursor: pointer;
}

.assistant-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field label { font-size: 0.8rem; color: #334155; }
.field input {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
}

.segmented {
  display: inline-flex;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
}

.seg-btn {
  border: none;
  background: #f8fafc;
  color: #334155;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
}
.seg-btn.active { background: #0b1f3a; color: #fff; }

.assistant-prompt { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.65rem; }
.assistant-prompt textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.6rem;
  resize: vertical;
}

.prompt-actions { display: flex; gap: 0.5rem; }
.query-btn, .clear-btn {
  border: none;
  border-radius: 8px;
  padding: 0.45rem 0.8rem;
  cursor: pointer;
}
.query-btn { background: #1d4ed8; color: #fff; }
.clear-btn { background: #e2e8f0; color: #0f172a; }

.error { color: #b91c1c; font-weight: 600; }
.answer { margin: 0.45rem 0 0.7rem; color: #0f172a; white-space: pre-line; }
.intent-badge {
  display: inline-block;
  margin: 0.2rem 0 0.25rem;
  font-size: 0.74rem;
  font-weight: 700;
  color: #334155;
  background: #e2e8f0;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
}
.audit-badge {
  display: inline-block;
  margin: 0 0 0.45rem 0.35rem;
  padding: 0.22rem 0.45rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 0.72rem;
  font-weight: 700;
}

.quick-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.quick-btn {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #1e293b;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  font-size: 0.72rem;
  cursor: pointer;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.7rem;
}
.summary-item {
  border: 1px solid #dbe3f0;
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.summary-item span { font-size: 0.75rem; color: #64748b; }

.evidence-card {
  border: 1px solid #dbe3f0;
  border-radius: 10px;
  padding: 0.6rem;
  margin-bottom: 0.65rem;
}
.evidence-card h4 { margin: 0 0 0.45rem; font-size: 0.9rem; }
.evidence-card table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.evidence-card th, .evidence-card td {
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  padding: 0.32rem 0.3rem;
  vertical-align: top;
}

.suggestions h4 { margin: 0.45rem 0; font-size: 0.9rem; }
.rule-meta {
  color: #475569;
  font-size: 0.8rem;
}
.suggestion-btn {
  display: block;
  width: 100%;
  text-align: left;
  margin-bottom: 0.35rem;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  cursor: pointer;
}

@media (max-width: 980px) {
  .assistant-drawer {
    width: 100%;
    min-width: 0;
  }
}
</style>
